import { GridElement, Connection, Light, ElementAction } from '../types';
import { recalculateAllConnectionLengths } from '../utils/converter';

interface ElementManagementProps {
  elements: GridElement[];
  connections: Connection[];
  selectedElement: GridElement | null;
  selectedConnection: Connection | null;
  setElements: React.Dispatch<React.SetStateAction<GridElement[]>>;
  setConnections: React.Dispatch<React.SetStateAction<Connection[]>>;
  setSelectedElement: React.Dispatch<React.SetStateAction<GridElement | null>>;
  setSelectedConnection: React.Dispatch<React.SetStateAction<Connection | null>>;
}

export const useElementManagement = ({
  elements,
  connections,
  selectedElement,
  selectedConnection,
  setElements,
  setConnections,
  setSelectedElement,
  setSelectedConnection
}: ElementManagementProps) => {

  /**
   * Deletes an element and all its connections
   * Also clears selection if the deleted element was selected
   */
  const deleteElement = (id: string) => {
    // Mark element as TO_DELETE if it exists in the database, otherwise remove it completely
    setElements(prev => prev.map(el => {
      if (el.id === id) {
        // If it's a new element (TO_CREATE), remove it completely
        if (el.action === ElementAction.TO_CREATE) {
          return null; // Will be filtered out below
        }
        // If it exists in database, mark for deletion
        return { ...el, action: ElementAction.TO_DELETE };
      }
      return el;
    }).filter(Boolean) as GridElement[]); // Remove null elements
    
    // Mark connections as TO_DELETE or remove them completely
    setConnections(prev => prev.map(conn => {
      if (conn.fromId === id || conn.toId === id) {
        // If it's a new connection (TO_CREATE), remove it completely
        if (conn.action === ElementAction.TO_CREATE) {
          return null; // Will be filtered out below
        }
        // If it exists in database, mark for deletion
        return { ...conn, action: ElementAction.TO_DELETE };
      }
      return conn;
    }).filter(Boolean) as Connection[]); // Remove null connections
    
    setSelectedElement(null); // Clear selection
  };

  /**
   * Deletes a specific connection
   * Removes it immediately from the interface and marks for deletion if it exists in the database
   */
  const deleteConnection = (connectionId: string) => {
    setConnections(prev => {
      const connectionToDelete = prev.find(conn => conn.id === connectionId);
      
      // Remove the connection immediately from the interface
      const filteredConnections = prev.filter(conn => conn.id !== connectionId);
      
      // If it's a new connection (TO_CREATE), it's already removed
      // If it exists in database, we need to track it for deletion
      if (connectionToDelete && connectionToDelete.action !== ElementAction.TO_CREATE) {
        // Add it back with TO_DELETE action so it gets deleted from database on save
        const deletedConnection = { ...connectionToDelete, action: ElementAction.TO_DELETE };
        return [...filteredConnections, deletedConnection];
      }
      
      return filteredConnections;
    });
    
    // Clear selection if the deleted connection was selected
    setSelectedConnection(null);
  };

  /**
   * Updates properties of an existing connection
   * Also updates the selected connection if it's the one being modified
   * Prevents duplication by ensuring atomic updates
   */
  const updateConnection = (id: string, updates: Partial<Connection>) => {
    console.log('🔗 useElementManagement updateConnection called:', id, updates);
    
    // Find the connection to update
    const connectionToUpdate = connections.find(conn => conn.id === id);
    if (!connectionToUpdate) {
      console.warn('🔗 Connection not found for update:', id);
      return;
    }
    
    setConnections(prev => prev.map(conn => {
      if (conn.id === id) {
        const updatedConnection = { ...conn, ...updates };
        // Mark as TO_UPDATE if it's not already TO_CREATE or TO_DELETE
        if (conn.action !== ElementAction.TO_CREATE && conn.action !== ElementAction.TO_DELETE) {
          updatedConnection.action = ElementAction.TO_UPDATE;
        }
        console.log('🔗 useElementManagement updated connection:', updatedConnection);
        return updatedConnection;
      }
      return conn;
    }));
    
    // Update selected connection if it's the one being modified
    if (selectedConnection?.id === id) {
      setSelectedConnection(prev => {
        if (!prev) return null;
        const updatedConnection = { ...prev, ...updates };
        // Mark as TO_UPDATE if it's not already TO_CREATE or TO_DELETE
        if (prev.action !== ElementAction.TO_CREATE && prev.action !== ElementAction.TO_DELETE) {
          updatedConnection.action = ElementAction.TO_UPDATE;
        }
        return updatedConnection;
      });
    }
  };

  /**
   * Recalculates lengths for all connections based on current element positions
   * This should be called whenever elements are moved
   */
  const recalculateConnectionLengths = () => {
    console.log('🔗 Recalculating connection lengths...');
    const updatedConnections = recalculateAllConnectionLengths(connections, elements);
    
    setConnections(updatedConnections);
    
    // Update selected connection if it exists
    if (selectedConnection) {
      const updatedSelectedConnection = updatedConnections.find(conn => conn.id === selectedConnection.id);
      if (updatedSelectedConnection) {
        setSelectedConnection(updatedSelectedConnection);
      }
    }
    
    console.log('🔗 Connection lengths recalculated');
  };

  /**
   * Updates properties of an existing element
   * Also updates the selected element if it's the one being modified
   * Prevents duplication by ensuring atomic updates
   */
  const updateElement = (id: string, updates: Partial<GridElement>) => {
    console.log('🔧 useElementManagement updateElement called:', id, updates);
    
    // Find the element to update
    const elementToUpdate = elements.find(el => el.id === id);
    if (!elementToUpdate) {
      console.warn('🔧 Element not found for update:', id);
      return;
    }
    
    setElements(prev => {
      const updatedElements = prev.map(el => {
        if (el.id === id) {
          // Exclure le champ `action` des updates pour éviter qu'un snapshot React décalé
          // n'écrase l'action gérée par ce hook (ex: handleSaveElement passe le full element).
          const { action: _ignored, ...safeUpdates } = updates as any;
          const updatedElement = { ...el, ...safeUpdates };
          // Mark as TO_UPDATE if it's not already TO_CREATE or TO_DELETE
          if (el.action !== ElementAction.TO_CREATE && el.action !== ElementAction.TO_DELETE) {
            updatedElement.action = ElementAction.TO_UPDATE;
          }
          console.log('🔧 useElementManagement updated element:', updatedElement);
          return updatedElement;
        }
        return el;
      });
      
      // Si l'élément a été déplacé (coordonnées GPS ou canvas changées), recalculer les longueurs des connexions
      const isPositionUpdate = updates.gpsCoordinates || updates.x !== undefined || updates.y !== undefined;
      if (isPositionUpdate) {
        console.log('🔧 Element position updated, recalculating connection lengths...');
        // Utiliser setTimeout pour éviter les problèmes de state update
        setTimeout(() => {
          const updatedConnections = recalculateAllConnectionLengths(connections, updatedElements);
          setConnections(updatedConnections);
          
          // Update selected connection if it exists
          if (selectedConnection) {
            const updatedSelectedConnection = updatedConnections.find(conn => conn.id === selectedConnection.id);
            if (updatedSelectedConnection) {
              setSelectedConnection(updatedSelectedConnection);
            }
          }
        }, 0);
      }
      
      return updatedElements;
    });
    
    // Update selected element if it's the one being modified
    if (selectedElement?.id === id) {
      setSelectedElement(prev => {
        if (!prev) return null;
        const updatedElement = { ...prev, ...updates };
        // Mark as TO_UPDATE if it's not already TO_CREATE or TO_DELETE
        if (prev.action !== ElementAction.TO_CREATE && prev.action !== ElementAction.TO_DELETE) {
          updatedElement.action = ElementAction.TO_UPDATE;
        }
        return updatedElement;
      });
    }
  };

  /**
   * Adds a new light to a pole element
   * @param elementId - ID of the pole element to add light to
   * @param lightType - Type of light to add (must match lightTypes configuration)
   */
  const addLight = (elementId: string, lightType: string, lightTypes: Array<{ id: string; label: string; power: number }>) => {
    // Find the light type configuration
    const lightTypeData = lightTypes.find(lt => lt.id === lightType);
    if (!lightTypeData) return; // Invalid light type

    // Create new light with default properties
    const newLight: Light = {
      id: `l${Date.now()}`, // Generate unique ID
      type: lightType as 'street-light' | 'flood-light' | 'decorative',
      power: lightTypeData.power, // Use configured power rating
      status: 'on' // Default to on
    };

    // Add light to the element
    setElements(prev => prev.map(el => {
      if (el.id === elementId) {
        const updatedElement = { ...el, lights: [...(el.lights || []), newLight] };
        // Mark as TO_UPDATE if it's not already TO_CREATE or TO_DELETE
        if (el.action !== ElementAction.TO_CREATE && el.action !== ElementAction.TO_DELETE) {
          updatedElement.action = ElementAction.TO_UPDATE;
        }
        return updatedElement;
      }
      return el;
    }));

    // Update selected element if it's the one being modified
    if (selectedElement?.id === elementId) {
      setSelectedElement(prev => {
        if (!prev) return null;
        const updatedElement = { ...prev, lights: [...(prev.lights || []), newLight] };
        // Mark as TO_UPDATE if it's not already TO_CREATE or TO_DELETE
        if (prev.action !== ElementAction.TO_CREATE && prev.action !== ElementAction.TO_DELETE) {
          updatedElement.action = ElementAction.TO_UPDATE;
        }
        return updatedElement;
      });
    }
  };

  /**
   * Removes a light from a pole element
   * @param elementId - ID of the pole element
   * @param lightId - ID of the light to remove
   */
  const removeLight = (elementId: string, lightId: string) => {
    console.log('🗑️ removeLight called:', { elementId, lightId });
    
    // Mark light as TO_DELETE instead of removing it directly
    setElements(prev => prev.map(el => {
      if (el.id === elementId) {
        const updatedLights = (el.lights || []).map(light => 
          light.id === lightId ? { ...light, action: ElementAction.TO_DELETE } : light
        );
        const updatedElement = { ...el, lights: updatedLights };
        // Mark element as TO_UPDATE if it's not already TO_CREATE or TO_DELETE
        if (el.action !== ElementAction.TO_CREATE && el.action !== ElementAction.TO_DELETE) {
          updatedElement.action = ElementAction.TO_UPDATE;
        }
        console.log('🗑️ Updated element with light marked for deletion:', updatedElement);
        return updatedElement;
      }
      return el;
    }));

    // Update selected element if it's the one being modified
    if (selectedElement?.id === elementId) {
      setSelectedElement(prev => {
        if (!prev) return null;
        const updatedLights = (prev.lights || []).map(light => 
          light.id === lightId ? { ...light, action: ElementAction.TO_DELETE } : light
        );
        const updatedElement = { ...prev, lights: updatedLights };
        // Mark element as TO_UPDATE if it's not already TO_CREATE or TO_DELETE
        if (prev.action !== ElementAction.TO_CREATE && prev.action !== ElementAction.TO_DELETE) {
          updatedElement.action = ElementAction.TO_UPDATE;
        }
        console.log('🗑️ Updated selected element with light marked for deletion:', updatedElement);
        return updatedElement;
      });
    }
  };

  /**
   * Updates properties of a specific light on a pole element
   * @param elementId - ID of the pole element
   * @param lightId - ID of the light to update
   * @param updates - Partial light object with properties to update
   */
  const updateLight = (elementId: string, lightId: string, updates: Partial<Light>) => {
    console.log('✏️ updateLight called:', { elementId, lightId, updates });
    
    // Update light properties in the element
    setElements(prev => prev.map(el => {
      if (el.id === elementId) {
        const updatedElement = { 
          ...el, 
          lights: (el.lights || []).map(light => 
            light.id === lightId ? { 
              ...light, 
              ...updates,
              // Mark light as TO_UPDATE if it's not already TO_CREATE or TO_DELETE
              action: light.action === ElementAction.TO_CREATE || light.action === ElementAction.TO_DELETE 
                ? light.action 
                : ElementAction.TO_UPDATE
            } : light
          )
        };
        // Mark element as TO_UPDATE if it's not already TO_CREATE or TO_DELETE
        if (el.action !== ElementAction.TO_CREATE && el.action !== ElementAction.TO_DELETE) {
          updatedElement.action = ElementAction.TO_UPDATE;
        }
        console.log('✏️ Updated element with light marked for update:', updatedElement);
        return updatedElement;
      }
      return el;
    }));

    // Update selected element if it's the one being modified
    if (selectedElement?.id === elementId) {
      setSelectedElement(prev => {
        if (!prev) return null;
        const updatedElement = { 
          ...prev, 
          lights: (prev.lights || []).map(light => 
            light.id === lightId ? { 
              ...light, 
              ...updates,
              // Mark light as TO_UPDATE if it's not already TO_CREATE or TO_DELETE
              action: light.action === ElementAction.TO_CREATE || light.action === ElementAction.TO_DELETE 
                ? light.action 
                : ElementAction.TO_UPDATE
            } : light
          )
        };
        // Mark element as TO_UPDATE if it's not already TO_CREATE or TO_DELETE
        if (prev.action !== ElementAction.TO_CREATE && prev.action !== ElementAction.TO_DELETE) {
          updatedElement.action = ElementAction.TO_UPDATE;
        }
        console.log('✏️ Updated selected element with light marked for update:', updatedElement);
        return updatedElement;
      });
    }
  };

  return {
    deleteElement,
    deleteConnection,
    updateElement,
    updateConnection,
    addLight,
    removeLight,
    updateLight,
    recalculateConnectionLengths
  };
};
